import React, { useEffect, useRef, useState } from 'react';
import EditDocumentModal from './modals/editDocumentModal';
import Button from './shared/Button';
import { useNavigate } from 'react-router-dom';
import Tooltip from './shared/Tooltip';

const STATUS_STYLES = {
	Draft: 'bg-draftColor text-dark-blue border border-draftColor',
	'In Review': 'bg-inReviewColor text-textOrange border border-inReviewColor',
	Finalized: 'bg-finalizedColor text-textBlue border border-finalizedColor',
	Approved: 'bg-approvedColor text-textGreen border border-approvedColor'
};

const PILL =
	'text-xs font-medium leading-100 px-2 py-2.5 rounded-lg inline-flex items-center gap-2.5 ';

const StatusIcon = ({ status }) => {
	switch (status) {
		case 'Draft':
			return (
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
					<circle cx="7" cy="7" r="6.5" stroke="#180A63" strokeDasharray="3 3" />
				</svg>
			);
		case 'In Review':
			return (
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
					<circle cx="7" cy="7" r="6.5" stroke="#EF7E22" />
					<path
						d="M7 12C7.65661 12 8.30679 11.8707 8.91342 11.6194C9.52004 11.3681 10.0712 10.9998 10.5355 10.5355C10.9998 10.0712 11.3681 9.52005 11.6194 8.91342C11.8707 8.30679 12 7.65661 12 7C12 6.34339 11.8707 5.69321 11.6194 5.08658C11.3681 4.47995 10.9998 3.92876 10.5355 3.46447C10.0712 3.00017 9.52004 2.63188 8.91342 2.3806C8.30679 2.12933 7.65661 2 7 2V7V12Z"
						fill="#EF7E22"
					/>
				</svg>
			);
		case 'Approved':
			return (
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M0 7C0 5.14348 0.737498 3.36301 2.05025 2.05025C3.36301 0.737498 5.14348 0 7 0C8.85652 0 10.637 0.737498 11.9497 2.05025C13.2625 3.36301 14 5.14348 14 7C14 8.85652 13.2625 10.637 11.9497 11.9497C10.637 13.2625 8.85652 14 7 14C5.14348 14 3.36301 13.2625 2.05025 11.9497C0.737498 10.637 0 8.85652 0 7ZM6.60053 9.996L10.6307 4.95787L9.90267 4.37547L6.46613 8.66973L4.032 6.6416L3.43467 7.3584L6.60053 9.996Z"
						fill="#108E56"
					/>
				</svg>
			);
		case 'Finalized':
			return (
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
					<circle cx="7" cy="7" r="6.5" stroke="#1A5BF8" />
					<path
						d="M2 7C2 7.98891 2.29324 8.95561 2.84265 9.77785C3.39206 10.6001 4.17295 11.241 5.08658 11.6194C6.00021 11.9978 7.00555 12.0969 7.97545 11.9039C8.94536 11.711 9.83627 11.2348 10.5355 10.5355C11.2348 9.83627 11.711 8.94536 11.9039 7.97545C12.0969 7.00555 11.9978 6.00021 11.6194 5.08658C11.241 4.17295 10.6001 3.39206 9.77785 2.84265C8.95561 2.29325 7.98891 2 7 2L7 7L2 7Z"
						fill="#1A5BF8"
					/>
				</svg>
			);
		default:
			return null;
	}
};

/** Small hook: close on outside click */
function useOnClickOutside(ref, handler) {
	useEffect(() => {
		const listener = (e) => {
			if (!ref.current || ref.current.contains(e.target)) return;
			handler();
		};
		document.addEventListener('mousedown', listener);
		document.addEventListener('touchstart', listener);
		return () => {
			document.removeEventListener('mousedown', listener);
			document.removeEventListener('touchstart', listener);
		};
	}, [ref, handler]);
}

const EditorTopBar = ({
	title,
	DocumentType,
	status,
	editedTime,
	format,
	university,
	isCreated,
	documentId,
	savingStatus,
	manualSavingStatus = 'idle', // Setting default value
	onManualSave,
	onEditDocumentClick,
	onDocumentUpdated,
	onPreviewPDF,
	onExportPDF
}) => {
	const [exportMenuOpen, setExportMenuOpen] = useState(false);
	const exportMenuRef = useRef(null);
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const navigate = useNavigate();

	useOnClickOutside(exportMenuRef, () => setExportMenuOpen(false));

	useEffect(() => {
		const onKey = (e) => e.key === 'Escape' && setExportMenuOpen(false);
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);

	// Prevent background scrolling when modal is open
	useEffect(() => {
		if (isSettingsOpen) {
			// Disable scrolling on body and main element
			const mainEl = document.querySelector('main');
			const originalBodyOverflow = document.body.style.overflow;
			const originalMainOverflow = mainEl ? mainEl.style.overflow : '';

			document.body.style.overflow = 'hidden';
			if (mainEl) {
				mainEl.style.overflow = 'hidden';
			}

			// Re-enable scrolling when modal closes
			return () => {
				document.body.style.overflow = originalBodyOverflow;
				if (mainEl) {
					mainEl.style.overflow = originalMainOverflow;
				}
			};
		}
	}, [isSettingsOpen]);

	const handlePreviewPDF = () => {
		setExportMenuOpen(false);
		onPreviewPDF();
	};

	const handleExportPDF = () => {
		setExportMenuOpen(false);
		onExportPDF();
	};

	const returnToDashboard = () => {
		navigate('/dashboard');
	};

	return (
		<div className="flex items-center justify-between px-6 py-7 bg-white rounded-lg border-1p border-[#F0F0F0] ">
			<div className="flex items-center space-x-4">
				{/* Back Arrow */}
				<div className="cursor-pointer" onClick={returnToDashboard}>
					<svg
						width="40"
						height="41"
						viewBox="0 0 40 41"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M19.0625 31.75L7.8125 20.5L19.0625 9.25M9.375 20.5H32.1875"
							stroke="#180A63"
							strokeWidth="3.75"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</div>

				<div>
					<div className="flex items-center gap-3 mb-2.5">
						<h1 className="text-28 font-semibold text-dark-blue leading-[1.3] truncate max-w-[150px] md:max-w-[200px] lg:max-w-[300px] xl:max-w-[450px]">
							{title}
						</h1>

						{savingStatus && (
							<div
								className={`
				flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium
				transition-all duration-300 ease-in-out
				${savingStatus === 'saving' && 'bg-gray-100 text-gray-600'}
				${savingStatus === 'saved' && 'bg-green-50 text-green-700'}
				${savingStatus === 'error' && 'bg-red-50 text-red-600'}
			`}
							>
								{savingStatus === 'saving' && (
									<>
										<div className="animate-spin h-3 w-3 border-2 border-gray-300 border-t-purple rounded-full" />
										<span>Saving</span>
									</>
								)}

								{savingStatus === 'saved' && (
									<>
										<svg
											width="14"
											height="14"
											viewBox="0 0 24 24"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M19.3333 6L9.66667 15.6667L4.66667 10.6667"
												stroke="currentColor"
												strokeWidth="2.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
										<span>Saved</span>
									</>
								)}

								{savingStatus === 'error' && (
									<span>Failed to save</span>
								)}
							</div>
						)}
					</div>

					<div className="flex items-center space-x-3 text-xs">
						{status && (
							<span
								className={`${PILL} ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-700 border border-gray-200'
									}`}
							>
								<StatusIcon status={status} />
								{status}
							</span>
						)}
						<span className="text-lg font-medium text-gray">{editedTime}</span>
						<span className="before:content-['•'] before:mr-2 before:text-gray text-lg font-medium text-gray">
							{format}
						</span>
						<span className="before:content-['•'] before:mr-2 before:text-gray text-lg font-medium text-gray">
							{/* {university ? university : 'Others'} */}
							{DocumentType ? DocumentType : 'Others'}
						</span>
					</div>
				</div>
			</div>

			<div className="flex items-center space-x-4">
				{isCreated && (
					<>
						<div>
							<Button
								onClick={onManualSave}
								disabled={manualSavingStatus === 'saving'}
								variant="outline-gradient-text"
								className="h-[45px] sm:h-[48px] min-w-[120px] px-6 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
							>
								<div className="flex items-center gap-2">
									{manualSavingStatus === 'saving' ? (
										<>
											<div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-purple rounded-full"></div>
											<span>Saving...</span>
										</>
									) : (
										<>
											<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16L21 8V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21Z" stroke="url(#save_btn_gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
												<path d="M17 21V13H7V21" stroke="url(#save_btn_gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
												<path d="M7 3V8H15" stroke="url(#save_btn_gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
												<defs>
													<linearGradient
														id="save_btn_gradient"
														x1="1"
														y1="3"
														x2="9"
														y2="3"
														gradientUnits="userSpaceOnUse"
													>
														<stop stopColor="#F36E49" />
														<stop offset="1" stopColor="#B67FE2" />
													</linearGradient>
												</defs>
											</svg>
											<span>{manualSavingStatus === 'saved' ? 'Saved' : 'Save'}</span>
										</>
									)}
								</div>
							</Button>
						</div>
						<div className="flex items-center space-x-4 transition-all duration-300 ease-out transform">
							{/* Manual Save Button */}

							<div className="flex items-center space-x-1 text-gray text-lg font-semibold leading-100 border-1p border-lightGrey px-6 py-3 gap-4 rounded-lg">
								<svg
									width="28"
									height="25"
									viewBox="0 0 28 25"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M8.93252 6.20741C7.97602 7.15007 7.24498 8.29675 6.79405 9.56173C6.34313 10.8267 6.18398 12.1773 6.32852 13.5124C6.37029 13.9082 6.25311 14.3045 6.00274 14.6139C5.75238 14.9233 5.38935 15.1206 4.99352 15.1624C4.59768 15.2042 4.20147 15.087 3.89203 14.8366C3.5826 14.5863 3.38529 14.2232 3.34352 13.8274C3.15194 12.0439 3.36568 10.2402 3.96878 8.5509C4.57188 6.86159 5.54877 5.33032 6.82652 4.07141C11.5305 -0.566593 19.1115 -0.488593 23.76 4.22441C28.4085 8.93741 28.3815 16.5214 23.679 21.1594C21.5023 23.3108 18.5792 24.5392 15.519 24.5884C14.3625 24.6092 13.2091 24.4627 12.0945 24.1534C11.9003 24.105 11.7177 24.0181 11.5576 23.898C11.3974 23.7779 11.2629 23.6269 11.162 23.454C11.0611 23.2811 10.9958 23.0898 10.97 22.8913C10.9442 22.6927 10.9585 22.4911 11.0118 22.2981C11.0652 22.1052 11.1566 21.9249 11.2808 21.7678C11.4049 21.6108 11.5592 21.4802 11.7347 21.3837C11.9101 21.2872 12.103 21.2268 12.3021 21.2061C12.5012 21.1853 12.7025 21.2047 12.894 21.2629C13.7308 21.4949 14.5968 21.6045 15.465 21.5884C17.7551 21.5529 19.943 20.6339 21.5715 19.0234C25.086 15.5584 25.119 9.87491 21.624 6.33191C18.129 2.78891 12.4455 2.74241 8.93252 6.20741Z"
										fill="#180A63"
									/>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M5.95068 15.0425C5.8197 15.1897 5.66101 15.3096 5.48368 15.3954C5.30634 15.4812 5.11384 15.5313 4.91716 15.5427C4.72048 15.5541 4.52347 15.5267 4.33739 15.462C4.15132 15.3973 3.97981 15.2965 3.83268 15.1655L1.25268 12.8705C0.955503 12.6059 0.775593 12.2342 0.752526 11.837C0.729459 11.4398 0.865124 11.0497 1.12968 10.7525C1.39423 10.4553 1.766 10.2754 2.1632 10.2523C2.56041 10.2293 2.9505 10.3649 3.24768 10.6295L5.82768 12.9245C5.97484 13.0555 6.09477 13.2142 6.1806 13.3915C6.26643 13.5688 6.31648 13.7613 6.3279 13.958C6.33932 14.1547 6.31189 14.3517 6.24716 14.5378C6.18244 14.7239 6.08169 14.8954 5.95068 15.0425Z"
										fill="#180A63"
									/>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M3.63008 14.8999C3.74827 15.0575 3.89634 15.1903 4.06584 15.2907C4.23534 15.391 4.42294 15.457 4.61795 15.4849C4.81295 15.5127 5.01153 15.5019 5.20235 15.453C5.39317 15.4041 5.57249 15.3181 5.73008 15.1999L8.73008 12.9499C9.04834 12.7112 9.25874 12.3559 9.315 11.9621C9.37126 11.5682 9.26877 11.1682 9.03008 10.8499C8.79138 10.5317 8.43604 10.3213 8.04221 10.265C7.64838 10.2088 7.24834 10.3112 6.93008 10.5499L3.93008 12.7999C3.77249 12.9181 3.63973 13.0662 3.53937 13.2357C3.439 13.4052 3.37301 13.5928 3.34515 13.7878C3.3173 13.9828 3.32812 14.1814 3.37701 14.3722C3.42589 14.563 3.51189 14.7424 3.63008 14.8999ZM15.0001 6.49994C15.3979 6.49994 15.7794 6.65797 16.0607 6.93928C16.342 7.22058 16.5001 7.60211 16.5001 7.99994V13.2499C16.5001 13.6478 16.342 14.0293 16.0607 14.3106C15.7794 14.5919 15.3979 14.7499 15.0001 14.7499C14.6023 14.7499 14.2207 14.5919 13.9394 14.3106C13.6581 14.0293 13.5001 13.6478 13.5001 13.2499V7.99994C13.5001 7.60211 13.6581 7.22058 13.9394 6.93928C14.2207 6.65797 14.6023 6.49994 15.0001 6.49994Z"
										fill="#180A63"
									/>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M21 13.2499C21 13.6478 20.842 14.0293 20.5607 14.3106C20.2794 14.5919 19.8978 14.7499 19.5 14.7499H15C14.6022 14.7499 14.2206 14.5919 13.9393 14.3106C13.658 14.0293 13.5 13.6478 13.5 13.2499C13.5 12.8521 13.658 12.4706 13.9393 12.1893C14.2206 11.908 14.6022 11.7499 15 11.7499H19.5C19.8978 11.7499 20.2794 11.908 20.5607 12.1893C20.842 12.4706 21 12.8521 21 13.2499Z"
										fill="#180A63"
									/>
								</svg>

								<span>Auto saves every 30 sec</span>
							</div>
							<Tooltip content="Document settings">
								<Button
									variant="outline-gradient-text"
									onClick={() => setIsSettingsOpen(true)}
									className="!h-[48px] !w-[50px] !p-0 !rounded-xl"
								>
									<svg
										width="20"
										height="20"
										viewBox="0 0 24 25"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										className="text-[#F36E49] transition-colors duration-200 group-hover:text-white"
									>
										<path
											d="M14.0794 2C14.2382 2.00001 14.3929 2.05041 14.5212 2.14394C14.6495 2.23748 14.7448 2.36933 14.7934 2.5205L15.6184 5.084C15.9649 5.2535 16.2964 5.444 16.6129 5.6585L19.2469 5.0915C19.4022 5.05834 19.564 5.07534 19.7091 5.14003C19.8541 5.20473 19.9748 5.31379 20.0539 5.4515L22.1329 9.05C22.2123 9.18763 22.2459 9.34693 22.2289 9.5049C22.212 9.66288 22.1452 9.81139 22.0384 9.929L20.2309 11.924C20.2572 12.3065 20.2572 12.6905 20.2309 13.073L22.0384 15.071C22.1452 15.1886 22.212 15.3371 22.2289 15.4951C22.2459 15.6531 22.2123 15.8124 22.1329 15.95L20.0539 19.55C19.9746 19.6874 19.8538 19.7962 19.7088 19.8606C19.5638 19.925 19.4021 19.9418 19.2469 19.9085L16.6129 19.3415C16.2979 19.5545 15.9649 19.7465 15.6199 19.916L14.7934 22.4795C14.7448 22.6307 14.6495 22.7625 14.5212 22.8561C14.3929 22.9496 14.2382 23 14.0794 23H9.92141C9.76262 23 9.60793 22.9496 9.47962 22.8561C9.35131 22.7625 9.256 22.6307 9.20741 22.4795L8.38391 19.9175C8.03835 19.7485 7.70504 19.5555 7.38641 19.34L4.75391 19.9085C4.5986 19.9417 4.43678 19.9247 4.29176 19.86C4.14673 19.7953 4.02598 19.6862 3.94691 19.5485L1.86791 15.95C1.78852 15.8124 1.75489 15.6531 1.77188 15.4951C1.78886 15.3371 1.85558 15.1886 1.96241 15.071L3.76991 13.073C3.74372 12.6914 3.74372 12.3086 3.76991 11.927L1.96241 9.929C1.85558 9.81139 1.78886 9.66288 1.77188 9.5049C1.75489 9.34693 1.78852 9.18763 1.86791 9.05L3.94691 5.45C4.0262 5.31256 4.14704 5.20381 4.29205 5.13939C4.43706 5.07497 4.59876 5.05821 4.75391 5.0915L7.38641 5.66C7.70441 5.4455 8.03741 5.252 8.38391 5.0825L9.20891 2.5205C9.25734 2.36982 9.3522 2.23832 9.47991 2.14482C9.60762 2.05133 9.76163 2.00064 9.91991 2H14.0779H14.0794ZM13.5304 3.5H10.4704L9.61841 6.1505L9.04391 6.431C8.76148 6.5692 8.48884 6.72657 8.22791 6.902L7.69691 7.262L4.97291 6.674L3.44291 9.326L5.31041 11.393L5.26541 12.029C5.24384 12.3426 5.24384 12.6574 5.26541 12.971L5.31041 13.607L3.43991 15.674L4.97141 18.326L7.69541 17.7395L8.22641 18.098C8.48734 18.2734 8.75998 18.4308 9.04241 18.569L9.61691 18.8495L10.4704 21.5H13.5334L14.3884 18.848L14.9614 18.569C15.2435 18.4311 15.5157 18.2738 15.7759 18.098L16.3054 17.7395L19.0309 18.326L20.5609 15.674L18.6919 13.607L18.7369 12.971C18.7585 12.6569 18.7585 12.3416 18.7369 12.0275L18.6919 11.3915L20.5624 9.326L19.0309 6.674L16.3054 7.259L15.7759 6.902C15.5157 6.72625 15.2435 6.56886 14.9614 6.431L14.3884 6.152L13.5319 3.5H13.5304ZM12.0004 8C13.1939 8 14.3385 8.47411 15.1824 9.31802C16.0263 10.1619 16.5004 11.3065 16.5004 12.5C16.5004 13.6935 16.0263 14.8381 15.1824 15.682C14.3385 16.5259 13.1939 17 12.0004 17C10.8069 17 9.66234 16.5259 8.81843 15.682C7.97451 14.8381 7.50041 13.6935 7.50041 12.5C7.50041 11.3065 7.97451 10.1619 8.81843 9.31802C9.66234 8.47411 10.8069 8 12.0004 8ZM12.0004 9.5C11.2048 9.5 10.4417 9.81607 9.87909 10.3787C9.31648 10.9413 9.00041 11.7044 9.00041 12.5C9.00041 13.2956 9.31648 14.0587 9.87909 14.6213C10.4417 15.1839 11.2048 15.5 12.0004 15.5C12.7961 15.5 13.5591 15.1839 14.1217 14.6213C14.6843 14.0587 15.0004 13.2956 15.0004 12.5C15.0004 11.7044 14.6843 10.9413 14.1217 10.3787C13.5591 9.81607 12.7961 9.5 12.0004 9.5Z"
											fill="currentColor"
										/>
									</svg>
								</Button>
							</Tooltip>
						</div>
					</>
				)}

				{/* Export Dropdown */}
				<div className="relative" ref={exportMenuRef}>
					<Button
						aria-label="Export options"
						aria-haspopup="menu"
						aria-expanded={exportMenuOpen}
						onClick={() => setExportMenuOpen((v) => !v)}
						variant="outline-gradient-text"
						className="h-[45px] sm:h-[48px] rounded-md"
					>
						<div className="flex items-center">
							<span>Export</span>
							<svg
								width="10"
								height="6"
								viewBox="0 0 10 6"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className="ml-2"
							>
								<path
									d="M1 1L5 5L9 1"
									stroke="url(#paint0_linear_297_2778)"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<defs>
									<linearGradient
										id="paint0_linear_297_2778"
										x1="1"
										y1="3"
										x2="9"
										y2="3"
										gradientUnits="userSpaceOnUse"
									>
										<stop stopColor="#F36E49" />
										<stop offset="1" stopColor="#B67FE2" />
									</linearGradient>
								</defs>
							</svg>
						</div>
					</Button>
					{exportMenuOpen && (
						<div
							role="menu"
							className="absolute right-0 z-[2001] mt-2 p-4 w-52 rounded-[15px] border border-[#E8E8EA] bg-white shadow-[2px_2px_20px_0_rgba(0,0,0,0.1)]"
						>
							<button
								role="menuitem"
								onClick={handlePreviewPDF}
								className="flex w-full items-center gap-[15px] text-base text-dark-blue hover:bg-gray-50 rounded-lg h-12 p-4"
							>
								<svg
									width="25"
									height="25"
									viewBox="0 0 25 25"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M6.78571 14.7857H4.5V20.5H10.2143V18.2143H6.78571V14.7857ZM4.5 10.2143H6.78571V6.78571H10.2143V4.5H4.5V10.2143ZM18.2143 18.2143H14.7857V20.5H20.5V14.7857H18.2143V18.2143ZM14.7857 4.5V6.78571H18.2143V10.2143H20.5V4.5H14.7857Z"
										fill="#180A63"
									/>
								</svg>
								Preview PDF
							</button>
							<div className="h-[15px]"></div> {/* Spacer for gap */}
							<button
								role="menuitem"
								onClick={handleExportPDF}
								className="flex w-full items-center gap-[15px] text-base text-[#B67FE2] hover:bg-gray-50 rounded-lg h-12 p-4"
							>
								<svg
									width="25"
									height="25"
									viewBox="0 0 25 25"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M11.5624 19.7916H13.3853V18.4895C14.2534 18.3333 14.9999 17.9947 15.6249 17.4739C16.2499 16.953 16.5624 16.1805 16.5624 15.1562C16.5624 14.427 16.3541 13.7586 15.9374 13.151C15.5208 12.5433 14.6874 12.0138 13.4374 11.5624C12.3958 11.2152 11.6753 10.9114 11.276 10.651C10.8767 10.3905 10.677 10.0346 10.677 9.58325C10.677 9.13186 10.8378 8.77596 11.1593 8.51554C11.4808 8.25513 11.9451 8.12492 12.552 8.12492C13.1076 8.12492 13.5416 8.25929 13.8541 8.52804C14.1666 8.79679 14.3923 9.13117 14.5312 9.53117L16.1978 8.85408C16.0069 8.24645 15.6555 7.71693 15.1437 7.26554C14.6319 6.81415 14.0631 6.56242 13.4374 6.51033V5.20825H11.6145V6.51033C10.7464 6.70131 10.0694 7.08325 9.58325 7.65617C9.09714 8.22908 8.85409 8.87145 8.85409 9.58325C8.85409 10.3992 9.09298 11.0589 9.57075 11.5624C10.0485 12.0659 10.7992 12.4999 11.8228 12.8645C12.9166 13.2638 13.6763 13.6197 14.102 13.9322C14.5277 14.2447 14.7402 14.6527 14.7395 15.1562C14.7395 15.7291 14.5357 16.1503 14.128 16.4197C13.7204 16.6892 13.2298 16.8235 12.6562 16.8228C12.0826 16.8221 11.5746 16.6444 11.1322 16.2895C10.6898 15.9346 10.3645 15.4006 10.1562 14.6874L8.43742 15.3645C8.68048 16.1978 9.05825 16.8708 9.57075 17.3833C10.0833 17.8958 10.7471 18.2471 11.5624 18.4374V19.7916ZM12.4999 22.9166C11.0589 22.9166 9.70478 22.643 8.43742 22.0958C7.17006 21.5485 6.06763 20.8065 5.13013 19.8697C4.19263 18.9329 3.45061 17.8305 2.90409 16.5624C2.35756 15.2944 2.08395 13.9402 2.08325 12.4999C2.08256 11.0596 2.35617 9.70547 2.90409 8.43742C3.452 7.16936 4.19402 6.06693 5.13013 5.13013C6.06624 4.19332 7.16867 3.45131 8.43742 2.90409C9.70617 2.35686 11.0603 2.08325 12.4999 2.08325C13.9395 2.08325 15.2937 2.35686 16.5624 2.90409C17.8312 3.45131 18.9336 4.19332 19.8697 5.13013C20.8058 6.06693 21.5482 7.16936 22.0968 8.43742C22.6454 9.70547 22.9187 11.0596 22.9166 12.4999C22.9145 13.9402 22.6409 15.2944 22.0958 16.5624C21.5506 17.8305 20.8086 18.9329 19.8697 19.8697C18.9308 20.8065 17.8284 21.5489 16.5624 22.0968C15.2964 22.6447 13.9423 22.918 12.4999 22.9166Z"
										fill="#B67FE2"
									/>
								</svg>
								Export as PDF
							</button>
						</div>
					)}
				</div>

				{!isCreated && (
					<Button
						onClick={onEditDocumentClick}
						variant="outline-gradient-text"
						className="h-[45px] sm:h-[48px] px-6 rounded-md"
					>
						<span className="text-lg leading-100">Sources</span>
					</Button>
				)}

				{/* Status Dropdown */}
				<div className="relative">
					<Button className="px-6 rounded-md h-[45px] sm:h-[48px]">
						<div className="flex items-center">
							<span>{status}</span>
							<svg
								width="10"
								height="6"
								viewBox="0 0 10 6"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className="ml-2"
							>
								<path
									d="M1 1L5 5L9 1"
									stroke="white"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</div>
					</Button>
					{/* Dropdown content will go here */}
				</div>
			</div>
			<EditDocumentModal
				isOpen={isSettingsOpen}
				onClose={() => setIsSettingsOpen(false)}
				documentId={documentId}
				currentTitle={title}
				onDocumentUpdated={onDocumentUpdated}
			/>
		</div>
	);
};

export default EditorTopBar;
